import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { consoleApi, type ReviewRow } from '@/lib/consoleApi';
import { StatusBadge } from '@/components/StatusBadge';
import { AdminPage } from '@/admin';

function Row({ row }: { row: ReviewRow }) {
  return (
    <Link to={`/registries/${encodeURIComponent(row.registry)}`} className="row">
      <div className="grow">
        <div className="rowtitle">{row.label}</div>
        <div className="rowsub">
          <span className="mono">{row.registry}</span> · v{row.version}
          {row.latest ? ` · last: ${row.latest.decision} by ${row.latest.reviewer}` : ' · never reviewed'}
        </div>
      </div>
      <StatusBadge status={row.status} />
      <span className="arr" aria-hidden>→</span>
    </Link>
  );
}

function Group({ title, rows, empty }: { title: string; rows: ReviewRow[]; empty: string }) {
  return (
    <section className="sect">
      <h2 className="sect-h">{title} <span>({rows.length})</span></h2>
      {rows.length
        ? <div className="rows">{rows.map((r) => <Row key={r.registry} row={r} />)}</div>
        : <p className="empty">{empty}</p>}
    </section>
  );
}

export function ReviewHomePage() {
  const q = useQuery({ queryKey: ['review-queue'], queryFn: () => consoleApi.reviewQueue() });
  const rows = q.data ?? [];
  // "Needs review" = anything not currently signed (pending / changes_requested / stale).
  const needsReview = rows.filter((r) => r.status !== 'signed');
  const signed = rows.filter((r) => r.status === 'signed');

  return (
    <AdminPage
      title="Review queue"
      description="Clinical sign-off worklist — the registries and provisional data awaiting Medical Advisory review. Open one to read it and record approval or request changes."
    >
      {q.isLoading ? <p className="text-body-sm text-text-muted">Loading…</p> : null}
      {q.isError ? <p className="text-body-sm text-flagged">Could not load the review queue.</p> : null}
      {q.data ? (
        <div data-testid="review-list">
          <Group title="Needs review" rows={needsReview} empty="Nothing awaiting review — all signed off." />
          <Group title="Signed" rows={signed} empty="No sign-offs recorded yet." />
        </div>
      ) : null}
    </AdminPage>
  );
}
